import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { GitMerge } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";

const inp = "bg-muted/60 border h-9 px-2 text-sm min-w-0";
const dt = (s) => new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" });

const Suggestions = ({ rows, onPick }) => (
  <div className="mb-3" data-testid="name-merge-suggestions">
    <div className="text-xs text-muted-foreground mb-1">Looks alike</div>
    {rows.map((s) => (
      <div key={`${s.from_name}-${s.to_name}`} className="flex flex-wrap items-center gap-2 text-sm py-1 border-t border-black/5">
        <span><b>{s.from_name}</b> ({s.from_count}) → <b>{s.to_name}</b> ({s.to_count})</span>
        <button data-testid={`name-merge-suggest-${s.from_name}`} onClick={() => onPick(s)} className="ml-auto border text-xs px-2 py-1 hover:border-primary">Review</button>
      </div>))}
  </div>
);

const Preview = ({ pv, onMerge, onCancel }) => (
  <div className="border p-3 mt-3 text-sm" data-testid="name-merge-preview">
    <p className="mb-2">Change <b>{pv.from_name}</b> to <b>{pv.to_name}</b> on <b data-testid="name-merge-total">{pv.total}</b> record(s):</p>
    <ul className="text-xs text-muted-foreground mb-2">{pv.rows.map((r) => <li key={r.label}>{r.label}: {r.count}</li>)}</ul>
    {pv.from_login && !pv.to_login && <p className="text-xs mb-2">{pv.from_login.name}'s login will be linked to "{pv.to_name}".</p>}
    {pv.conflict && <p className="text-xs text-rose-600 mb-2" data-testid="name-merge-conflict">Both spellings are linked to different logins ({pv.from_login.name} and {pv.to_login.name}). Unlink one first.</p>}
    <p className="text-xs text-muted-foreground mb-2">Future tracker imports will also use "{pv.to_name}". You can undo this below.</p>
    <div className="flex gap-2">
      <button data-testid="name-merge-confirm" disabled={pv.conflict} onClick={onMerge} className="bg-primary text-primary-foreground h-9 px-4 text-sm disabled:opacity-50">Merge</button>
      <button data-testid="name-merge-cancel" onClick={onCancel} className="border h-9 px-4 text-sm">Cancel</button>
    </div>
  </div>
);

const History = ({ rows, onUndo }) => (
  <div className="mt-3 text-xs" data-testid="name-merge-history">
    <div className="text-muted-foreground mb-1">Past merges</div>
    {rows.map((m) => (
      <div key={m.merge_id} className="flex flex-wrap items-center gap-2 py-1 border-t border-black/5" data-testid={`name-merge-${m.merge_id}`}>
        <span>{m.from_name} → {m.to_name} · {m.total} record(s) · {m.by} · {dt(m.at)}{m.undone_at ? ` · undone by ${m.undone_by}` : ""}</span>
        {!m.undone_at && <button data-testid={`name-merge-undo-${m.merge_id}`} onClick={() => onUndo(m)} className="ml-auto border px-2 py-0.5 hover:border-primary">Undo</button>}
      </div>))}
  </div>
);

export const NameMerge = ({ names, onDone }) => {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [f, setF] = useState({ from_name: "", to_name: "" });
  const [pv, setPv] = useState(null);
  const founder = user?.role_key === "founder";
  const load = useCallback(() => api.get("/admin/name-merges").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { if (founder) load(); }, [founder, load]);
  if (!founder || !d) return null;
  const review = (x) => api.get("/admin/name-merges/preview", { params: { from_name: x.from_name, to_name: x.to_name } })
    .then((r) => { setF({ from_name: x.from_name, to_name: x.to_name }); setPv(r.data); }).catch((e) => toast.error(errMsg(e)));
  const done = () => { setPv(null); setF({ from_name: "", to_name: "" }); load(); onDone(); };
  const merge = () => api.post("/admin/name-merges", f).then((r) => { toast.success(`${r.data.total} record(s) now say ${f.to_name}`); done(); }).catch((e) => toast.error(errMsg(e)));
  const undo = (m) => window.confirm(`Undo merging ${m.from_name} into ${m.to_name}?`) && api.post(`/admin/name-merges/${m.merge_id}/undo`)
    .then((r) => { toast.success(`${r.data.restored} record(s) back to ${m.from_name}${r.data.skipped ? ` · ${r.data.skipped} changed again since, left as they are` : ""}`); done(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="ax-panel p-4 mb-4" data-testid="name-merge-panel">
      <div className="flex items-center gap-2 font-semibold mb-1"><GitMerge className="h-4 w-4 text-primary" />Merge name spellings</div>
      <p className="text-xs text-muted-foreground mb-3">When one person is spelled two ways (like Rachna and Rachana), keep one spelling. Every lead, UID, production step and follow-up changes to it, so they count once on the leaderboard.</p>
      {d.suggestions.length > 0 && <Suggestions rows={d.suggestions} onPick={review} />}
      <datalist id="merge-names">{names.map((n) => <option key={n.name} value={n.name} />)}</datalist>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        Change <input list="merge-names" data-testid="name-merge-from" value={f.from_name} onChange={(e) => setF({ ...f, from_name: e.target.value })} className={inp} placeholder="Spelling to replace" />
        to <input list="merge-names" data-testid="name-merge-to" value={f.to_name} onChange={(e) => setF({ ...f, to_name: e.target.value })} className={inp} placeholder="Spelling to keep" />
        <button data-testid="name-merge-review" disabled={!f.from_name.trim() || !f.to_name.trim()} onClick={() => review(f)} className="border h-9 px-4 text-sm disabled:opacity-50">Review</button>
      </div>
      {pv && <Preview pv={pv} onMerge={merge} onCancel={() => setPv(null)} />}
      {d.merges.length > 0 && <History rows={d.merges} onUndo={undo} />}
    </div>
  );
};
